import React, { useState, useEffect, useCallback } from "react";
import GenericTable from "../GenericTable/GenericTable";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Alert,
} from "@mui/material";
import axios from "axios";
import config from "../../../config";

// ✅ Format date from ISO format (2025-12-02T00:00:00) to MM-DD-YYYY HH:MM:SS
const formatDate = (dateString) => {
  if (!dateString || dateString === "Never") return "Never";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Never";

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return "Never";
  }
};

const clientColumns = [
  { id: "client_name", label: "Client Name" },
  { id: "client_email_id", label: "Contact Person Email" },
  { id: "client_phone_number", label: "Contact Person Phone" },
  { id: "last_logged_in_date", label: "Last Logged In", render: (row) => formatDate(row.last_logged_in_date) },
  { id: "has_purchased", label: "Has Purchased" }
];

function ClientList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // ✅ UNIVERSAL FETCH: Server-side pagination
  const fetchClients = useCallback(async (pageNum = page, rowSize = rowsPerPage, filters = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: pageNum,
        limit: rowSize,
      };

      const response = await axios.get(
        `${config.API_BASE_URL_V1}/admin/client/list`,
        { params: queryParams }
      );

      if (response.data.success && response.data.data) {
        const transformedData = response.data.data.map((client) => ({
          client_id: client.client_id,
          client_name: client.client_name || "N/A",
          client_email_id: client.email_id || "N/A",
          client_phone_number: client.mobile_number || "N/A",
          last_logged_in_date: client.last_login || "Never",
          has_purchased: client.has_purchased ? "YES" : "NO"
        }));

        setRows(transformedData);
        // ✅ Get total from pagination object
        setTotalCount(response.data?.pagination?.total_records || 0);
      } else {
        setError(response.data.message || "Failed to fetch clients");
      }
    } catch (err) {
      console.error("Error fetching clients:", err);
      setError(`Error loading clients: ${err.message}`);
      setRows([]);
    }
    setLoading(false);
  }, [page, rowsPerPage]);

  // ✅ AUTO-FETCH: When page or rowsPerPage changes
  useEffect(() => {
    fetchClients(page, rowsPerPage, {});
  }, [page, rowsPerPage, fetchClients]);

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedRow) {
      try {
        const response = await fetch(
          `${config.API_BASE_URL_V1}/admin/client/${selectedRow.client_id}`,
          {
            method: "DELETE",
            headers: config.getHeaders(),
          }
        );

        const result = await response.json();
        console.log("Delete client response:", result);

        if (result.success) {
          // Remove from local state
          setRows((prevRows) =>
            prevRows.filter((r) => r.client_id !== selectedRow.client_id)
          );
          alert("Client deleted successfully!");
        } else {
          alert(result.message || "Failed to delete client");
        }
      } catch (error) {
        console.error("Error deleting client:", error);
        alert(`Error deleting client: ${error.message}`);
      }
    }
    handleClose();
  };

  const customActions = [
    {
      label: "View",
      icon: "visibility",
      color: "secondary",
    },
    {
      label: "Copy Link",
      icon: "content_copy",
      color: "primary",
      onClick: async (row) => {
        const link = `${window.location.origin}/client/${row.client_id}`;
        try {
          await navigator.clipboard.writeText(link);
          alert("Link copied to clipboard");
        } catch {
          const ta = document.createElement("textarea");
          ta.value = link;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
          alert("Link copied");
        }
      }
    }

  ];

  return (
    <>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && (
        <GenericTable
          title=""
          columns={clientColumns}
          rows={rows}
          showView={false}
          showEdit={true}
          showDelete={true}
          onEdit={(row) => navigate(`/client-management/edit-client/${row.client_id}`)}
          onDelete={handleDeleteClick}
          isServerSide={true}
          currentPage={page - 1}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={(_, newPage) => {
            setPage(newPage + 1);
          }}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            setRowsPerPage(newRowsPerPage);
            setPage(1);
          }}
          customHeaderButtons={[
            {
              label: "Add New Client",
              icon: "add",
              onClick: () => navigate(`/client-management/create-client`)
            }
          ]}
        />
      )}
      {/* ✅ Professional Delete Confirmation Modal */}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{selectedRow?.client_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined" color="secondary">
            No
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ClientList;
